import { LiteratureReviewPlan } from "@core/domain/models/literature-review-plan.model";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { LevelOfEvidence } from "@core/domain/types/levelOfEvidence.type";

export function generateMedicalBackgroundSearchQuery(
  importSourceId: string,
  plan: LiteratureReviewPlan,
) {
  if (importSourceId === BuiltInImportSourceId.PUBMED) {
    return generatePubmedMedicalBackgroundSearchQuery(plan);
  }
  if (importSourceId === BuiltInImportSourceId.COCHRANE) {
    return generateCochraneMedicalBackgroundSearchQuery(plan);
  }
  if (importSourceId === BuiltInImportSourceId.PROSPERO) {
    return generateProsperoMedicalBackgroundSearchQuery(plan);
  }
  return "";
}

function generatePubmedMedicalBackgroundSearchQuery(plan: LiteratureReviewPlan) {
  const loe = plan?.targetLoe;
  const conditionNames = [...(plan?.alternativeConditionNames ?? []), plan?.conditionName];

  const yearsToSubtract = plan?.timeRange ?? 5;
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - yearsToSubtract);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);
  const timeRangeQuery = `(${formattedStartDate}:${formattedEndDate}[pdat])`;

  const conditionNamesQuery = `(${conditionNames
    .filter((name) => !!name)
    .map((name) => `"${name}"[Title]`)
    .join(" OR ")})`;

  switch (loe) {
    case LevelOfEvidence.A:
      return `${conditionNamesQuery} AND ("guideline"[Title] OR "Practice Guideline"[Publication Type] OR "Clinical Protocols"[MeSH] OR "Evidence-Based Practice"[MeSH]) AND ${timeRangeQuery}`;
    case LevelOfEvidence.B:
      return `${conditionNamesQuery} AND ("systematic review"[Title] OR "Review Literature as Topic"[MeSH] OR "meta-analysis"[Title] OR "Meta-Analysis"[Publication Type]) AND ${timeRangeQuery}`;
    case LevelOfEvidence.C:
      return `${conditionNamesQuery} AND ("randomized controlled trial"[Publication Type] OR "Randomized Controlled Trials as Topic"[MeSH] OR “random allocation” [MeSH] ) AND ${timeRangeQuery}`;
    case LevelOfEvidence.D:
      return `${conditionNamesQuery} AND ("randomized controlled trial"[Publication Type] OR “cohort studies” [MeSH] OR "Observational Studies as Topic"[MeSH] OR "Case-Control Studies”[MeSH] ) AND ${timeRangeQuery}`;
    case LevelOfEvidence.E:
      return `${conditionNamesQuery} AND (Case Reports[Publication Type])`;
  }
  return "";
}

function generateCochraneMedicalBackgroundSearchQuery(plan: LiteratureReviewPlan) {
  const conditionNames = [...(plan?.alternativeConditionNames ?? []), plan?.conditionName];

  const yearsToSubtract = plan?.timeRange ?? 5;
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - yearsToSubtract);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);
  const timeRangeQuery = `(${formattedStartDate} to ${formattedEndDate})`;

  const conditionNamesQuery = `(${conditionNames.filter((name) => !!name).join(" OR ")})`;

  return `${conditionNamesQuery} in Record Title  AND Date range: ${timeRangeQuery}`;
}

function generateProsperoMedicalBackgroundSearchQuery(plan: LiteratureReviewPlan) {
  const conditionNames = [...(plan?.alternativeConditionNames ?? []), plan?.conditionName];

  const yearsToSubtract = plan?.timeRange ?? 5;
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - yearsToSubtract);

  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);
  const timeRangeQuery = `(${formattedStartDate} to ${formattedEndDate})`;

  const conditionNamesQuery = `(${conditionNames.filter((name) => !!name).join(" OR ")})`;

  return `${conditionNamesQuery} in Title AND Review status: Completed AND Date of registration: ${timeRangeQuery}`;
}
